import LegalLayout, { LegalSection, LegalList } from "./LegalLayout";

export default function Terms() {
  return (
    <LegalLayout
      testid="legal-terms-page"
      kicker="Legal"
      title="Terms of Service"
      lastUpdated="28 February 2026"
      intro="These terms are the agreement between your business and UKCSL Ltd (trading as CanniComply). They set out what you can expect from us and what we expect from you. Please read them carefully — using the platform means you accept them."
    >
      <LegalSection number="01" title="The service" testid="legal-terms-service">
        <p>
          CanniComply is a staff-assurance platform for regulated businesses.
          It lets you upload policies and SOPs, generate learning content,
          assign training, record acknowledgements and mint verifiable
          certificates.
        </p>
        <p>
          You keep ownership of every document and every compliance record
          you create. We host and process them on your behalf under the
          terms below.
        </p>
      </LegalSection>

      <LegalSection number="02" title="Your account">
        <LegalList
          items={[
            "You must give accurate information at signup and keep it up to date.",
            "You are responsible for keeping your password secure.",
            "You are responsible for every action taken by users you add to your workspace.",
            "You must be at least 18 to hold an owner account.",
          ]}
        />
      </LegalSection>

      <LegalSection number="03" title="Acceptable use">
        <p>You agree not to:</p>
        <LegalList
          items={[
            "Upload content you do not have the right to distribute internally.",
            "Use the service to store or process special-category personal data unless expressly agreed with us in writing.",
            "Attempt to reverse-engineer, scrape, or interfere with the operation of the platform.",
            "Impersonate another organisation or user.",
            "Use the service to violate any applicable law.",
          ]}
        />
      </LegalSection>

      <LegalSection number="04" title="AI-generated content">
        <p>
          The platform uses artificial intelligence to draft learning
          modules and knowledge-check questions from your SOPs. All AI drafts
          are marked as such and require human review and approval by an
          owner or manager before they become live training material.
        </p>
        <p>
          Because AI can occasionally misinterpret source material, you
          remain responsible for verifying that every published module and
          question accurately reflects your policy. We provide the drafting;
          you own the sign-off.
        </p>
      </LegalSection>

      <LegalSection number="05" title="Certificates & verification">
        <p>
          Certificates minted by the platform carry a unique verification ID
          and a QR code that resolves against our public{" "}
          <span className="font-mono text-white">/verify/{"{cert_id}"}</span>{" "}
          endpoint. We keep this endpoint live for the lifetime of your
          subscription and for the retention period thereafter, so an
          inspector can always confirm authenticity.
        </p>
      </LegalSection>

      <LegalSection number="06" title="Fees & billing">
        <p>
          Subscription fees are billed per your plan. Fees are non-refundable
          except where required by law. We may adjust prices with at least
          30 days' notice. Non-payment for 14 days may result in workspace
          suspension; your data remains preserved during suspension and can
          be restored when the account is brought back into good standing.
        </p>
      </LegalSection>

      <LegalSection number="07" title="Availability">
        <p>
          We target 99.9% availability for the platform, measured monthly
          excluding scheduled maintenance. In the unlikely event of an
          extended outage, we will communicate promptly and, where relevant,
          apply service credits per your contract.
        </p>
      </LegalSection>

      <LegalSection number="08" title="Limitation of liability">
        <p>
          Nothing in these terms limits our liability for death or personal
          injury caused by our negligence, fraud, or any other liability
          that cannot be limited by law. Subject to that, our aggregate
          liability in any 12-month period is capped at the fees you paid
          us for the platform in that period.
        </p>
        <p>
          We are not liable for indirect, incidental, or consequential
          losses, or for lost profits, lost revenue, or lost goodwill.
        </p>
      </LegalSection>

      <LegalSection number="09" title="Termination">
        <p>
          Either of us may end the contract at any time with 30 days' notice.
          On termination we make your data available for export for a
          further 60 days, after which it is deleted or anonymised.
        </p>
      </LegalSection>

      <LegalSection number="10" title="Governing law">
        <p>
          These terms are governed by the laws of England and Wales. Any
          dispute is subject to the exclusive jurisdiction of the English
          courts, except where a mandatory local consumer-protection law
          applies to you.
        </p>
      </LegalSection>
    </LegalLayout>
  );
}
